<p class="text-label-small -mt-4">[{{ useAviaryButtonComponents ? 'Using Aviary button components' : 'Using native &lt;button> elements' }}]</p>

<mx-action-buttons>
  <button mat-raised-button (click)="allButtonsEnabled = !allButtonsEnabled;">
    {{ allButtonsEnabled ? 'Disable All' : 'Enable All' }}
  </button>
</mx-action-buttons>

<div> <!-- This div is just to defeat the default flex y-gap. -->
  @if (hasMultipleColorOptions) {
    <mx-radio-group-form-field fieldName="buttonColor" label="Button color:" [options]="colorOptions" [formGroup]="formGroup"
                               class="no-bottom-margin"/>
  }

  <mx-radio-group-form-field fieldName="showButtonTypes" label="Show:" [options]="buttonTypeOptions" [formGroup]="formGroup" class="no-bottom-margin"/>

  @if (hasMultipleBackgroundColorOptions) {
    <mx-radio-group-form-field fieldName="backgroundColor" label="Background color:" [options]="backgroundColorOptions" [formGroup]="formGroup"
                               class="no-bottom-margin"/>
  }

  <mx-radio-group-form-field fieldName="iconPosition" label="Icon position:" [options]="iconPositionOptions" [formGroup]="formGroup"
                             class="no-bottom-margin"/>

  @if (useAviaryButtonComponents) {
    <mx-radio-group-form-field fieldName="labelAffixes" label="Label affixes:" [options]="labelAffixOptions" [formGroup]="formGroup"
                               class="no-bottom-margin"/>
    <mx-radio-group-form-field fieldName="tooltips" label="Tooltips:" [options]="tooltipOptions" [formGroup]="formGroup"
                               class="no-bottom-margin"/>
    <mx-radio-group-form-field fieldName="extraContent" label="Special content:" [options]="extraContentOptions" [formGroup]="formGroup"
                               class="no-bottom-margin"/>
  }
</div>

@let startIcon = selectedIconPosition === 'start' || selectedIconPosition === 'both' ? 'add' : undefined;
@let endIcon = selectedIconPosition === 'end' || selectedIconPosition === 'both' ? 'arrow_forward' : undefined;
@let customContentTemplate = selectedExtraContent === 'custom' ? customButtonContent : undefined;
@let beforeLabel = selectedLabelAffixes === 'before' || selectedLabelAffixes === 'both' ? '← ' : undefined;
@let afterLabel = selectedLabelAffixes === 'after' || selectedLabelAffixes === 'both' ? ' →' : undefined;
@let labelSize = selectedExtraContent === 'small-label' ? 'small' : 'default';
@let tooltip = selectedTooltips === 'same-for-both'
  ? 'Shared button tooltip'
  : selectedTooltips === 'different-for-each' ? 'Enabled button tooltip' : '';
@let disabledTooltip = selectedTooltips === 'disabled-only' || selectedTooltips === 'different-for-each' ? 'Disabled button tooltip' : '';

<ng-template #customButtonContent>
  <mat-icon class="material-icons-outlined">pets</mat-icon>
</ng-template>

<p class="italic" [class.line-through]="!allButtonsEnabled">
  To view keyboard focus states, click
  <mx-link-button linkType="inline" label="here"/>
  , then tab around.
</p>

<div class="flex flex-row justify-between items-stretch gap-x-4">
  <div class="{{selectedBackgroundColor}} rounded-xl mb-8">
    <table class="border-separate border-spacing-6">
      <caption></caption>  <!-- To appease SonarLint. -->

      <thead class="bg-surface text-left align-top text-title-medium">
      <th>Filled<br/><span class="text-label-small">{{ useAviaryButtonComponents ? '&lt;mx-filled-button>' : '&lt;button mat-flat-button>' }}</span></th>
      <th>Outlined<br/><span class="text-label-small">{{ useAviaryButtonComponents ? '&lt;mx-outlined-button>' : '&lt;button mat-stroked-button>' }}</span></th>
      <th>Text<br/><span class="text-label-small">{{ useAviaryButtonComponents ? '&lt;mx-text-button>' : '&lt;button mat-button>' }}</span></th>
      <th>Elevated<br/><span class="text-label-small">{{ useAviaryButtonComponents ? '&lt;mx-elevated-button>' : '&lt;button mat-raised-button>' }}</span></th>
      <th>Split<br/><span class="text-label-small">&lt;mx-split-button></span></th>
      </thead>

      <tr>
        <!-- CONTENTS: TEXT ONLY -->
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('filled')) {
            @if (useAviaryButtonComponents) {
              <mx-filled-button [startIcon]="startIcon" [endIcon]="endIcon" label="Filled" [color]="selectedColor" [enabled]="allButtonsEnabled"
                                [customContentTemplate]="customContentTemplate"
                                [beforeLabel]="beforeLabel" [afterLabel]="afterLabel" [labelSize]="labelSize"
                                [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-flat-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                @if (startIcon) {
                  <mat-icon class="material-icons-outlined">{{ startIcon }}</mat-icon>
                }
                Filled
                @if (endIcon) {
                  <mat-icon class="material-icons-outlined">{{ endIcon }}</mat-icon>
                }
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('outlined')) {
            @if (useAviaryButtonComponents) {
              <mx-outlined-button [startIcon]="startIcon" [endIcon]="endIcon" label="Outlined" [color]="selectedColor" [enabled]="allButtonsEnabled"
                                  [customContentTemplate]="customContentTemplate"
                                  [beforeLabel]="beforeLabel" [afterLabel]="afterLabel" [labelSize]="labelSize"
                                  [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-stroked-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                @if (startIcon) {
                  <mat-icon class="material-icons-outlined">{{ startIcon }}</mat-icon>
                }
                Outlined
                @if (endIcon) {
                  <mat-icon class="material-icons-outlined">{{ endIcon }}</mat-icon>
                }
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('text')) {
            @if (useAviaryButtonComponents) {
              <mx-text-button [startIcon]="startIcon" [endIcon]="endIcon" label="Text" [color]="selectedColor" [enabled]="allButtonsEnabled"
                              [customContentTemplate]="customContentTemplate"
                              [beforeLabel]="beforeLabel" [afterLabel]="afterLabel" [labelSize]="labelSize"
                              [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                @if (startIcon) {
                  <mat-icon class="material-icons-outlined">{{ startIcon }}</mat-icon>
                }
                Text
                @if (endIcon) {
                  <mat-icon class="material-icons-outlined">{{ endIcon }}</mat-icon>
                }
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('elevated')) {
            @if (useAviaryButtonComponents) {
              <mx-elevated-button [startIcon]="startIcon" [endIcon]="endIcon" label="Elevated" [color]="selectedColor" [enabled]="allButtonsEnabled"
                                  [customContentTemplate]="customContentTemplate"
                                  [beforeLabel]="beforeLabel" [afterLabel]="afterLabel" [labelSize]="labelSize"
                                  [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-raised-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                @if (startIcon) {
                  <mat-icon class="material-icons-outlined">{{ startIcon }}</mat-icon>
                }
                Elevated
                @if (endIcon) {
                  <mat-icon class="material-icons-outlined">{{ endIcon }}</mat-icon>
                }
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('split')) {
            <mx-split-button [definition]="splitButtonDefinition" [color]="selectedColor" [enabled]="allButtonsEnabled"/>
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
      </tr>

      <tr>
        <!-- CONTENTS: ICON ONLY -->
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('filled')) {
            @if (useAviaryButtonComponents) {
              <mx-filled-button startIcon="more_horiz" [color]="selectedColor" [enabled]="allButtonsEnabled"
                                [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-flat-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                <mat-icon class="material-icons-outlined">more_horiz</mat-icon>
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('outlined')) {
            @if (useAviaryButtonComponents) {
              <mx-outlined-button startIcon="more_horiz" [color]="selectedColor" [enabled]="allButtonsEnabled"
                                  [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-stroked-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                <mat-icon class="material-icons-outlined">more_horiz</mat-icon>
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('text')) {
            @if (useAviaryButtonComponents) {
              <mx-text-button startIcon="more_horiz" [color]="selectedColor" [enabled]="allButtonsEnabled"
                              [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                <mat-icon class="material-icons-outlined">more_horiz</mat-icon>
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('elevated')) {
            @if (useAviaryButtonComponents) {
              <mx-elevated-button startIcon="more_horiz" [color]="selectedColor" [enabled]="allButtonsEnabled"
                                  [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-raised-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                <mat-icon class="material-icons-outlined">more_horiz</mat-icon>
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td>
          <span class="text-title-large">--</span>
        </td>
      </tr>
    </table>
  </div>

  <div class="flex flex-col gap-y-4 mb-8">
    <table class="{{selectedBackgroundColor}} rounded-xl border-separate border-spacing-6">
      <caption></caption>  <!-- To appease SonarLint. -->

      <thead class="bg-surface text-left align-top text-title-medium">
      <th>Icon<br/><span class="text-label-small">{{ useAviaryButtonComponents ? '&lt;mx-icon-button>' : '&lt;button mat-icon-button>' }}</span></th>
      <th>FAB<br/><span class="text-label-small">{{ useAviaryButtonComponents ? '&lt;mx-fab>' : '&lt;button mat-fab>' }}</span></th>
      </thead>

      <tr>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('icon')) {
            @if (useAviaryButtonComponents) {
              <mx-icon-button icon="notifications" [color]="selectedColor" [enabled]="allButtonsEnabled" [customContentTemplate]="customContentTemplate"
                              [showStatusDot]="selectedExtraContent === 'status-dot'" [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-icon-button [color]="selectedColor" [disabled]="!allButtonsEnabled">
                <mat-icon class="material-icons-outlined">notifications</mat-icon>
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
        <td class="text-left align-middle h-16">
          @if (supportsButtonType('fab')) {
            @if (useAviaryButtonComponents) {
              <mx-fab icon="create" [color]="selectedColor" [enabled]="allButtonsEnabled" [customContentTemplate]="customContentTemplate"
                      [tooltip]="tooltip" [disabledTooltip]="disabledTooltip"/>
            } @else {
              <button mat-fab [color]="selectedColor" [disabled]="!allButtonsEnabled">
                <mat-icon class="material-icons-outlined">create</mat-icon>
              </button>
            }
          } @else {
            <span class="text-title-large">--</span>
          }
        </td>
      </tr>
    </table>

    <div class="{{selectedBackgroundColor}} rounded-xl flex flex-col items-start gap-y-3 p-6">
      <p class="text-title-medium">Link<br/><span class="text-label-small">&lt;mx-link-button></span></p>
      <mx-link-button label="Standalone external link" href="https://www.example.com/" [enabled]="allButtonsEnabled"/>
      <mx-link-button label="Standalone external arrow link" href="https://www.example.com/" endIcon="arrow_forward" [enabled]="allButtonsEnabled"/>
      <p class="text-body-large">
        This sentence contains an
        <span class="inline-flex items-baseline">
          <mx-link-button linkType="inline" label="inline external link" href="https://www.example.com/" [enabled]="allButtonsEnabled"/>
          <span>.</span>
        </span>
      </p>
      <p class="text-body-medium">
        This smaller sentence has an
        <mx-link-button linkType="inline" label="inline external link" href="https://www.example.com/" [enabled]="allButtonsEnabled"/>
        too.
      </p>
      <div class="flex flex-row justify-between items-center gap-x-3 w-full">
        <mx-link-button label="Action (not nav)" endIcon="arrow_forward" [enabled]="allButtonsEnabled" (click)="onClickLinkButton()"/>
        <span class="text-body-medium">Clicks: {{ linkButtonClickCount }}</span>
      </div>
    </div>
  </div>
</div>
